// eslint-disable-next-line no-restricted-imports
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Card,
  Combobox,
  Flex,
  Heading,
  MultiCombobox,
  Spinner,
  Text,
} from '@applyboard/crystal-ui';
import { useCurrentUser, useEventTracker } from '@applyboard/ui-utils';
import { DESTINATION_COUNTRIES_OPTIONS_LIST } from '../../../common/constants';

import { shouldShowUsdPricingNotice } from '../../../common/utils/shouldShowUsdPricingNotice';
import { ServiceCardsSection } from '../../../components/ServiceCardsSection/ServiceCardsSection';
import { useCountries } from '../../../data/countries/useCountries';

import { useGetStudentDetails } from '../../../data/studentDetails';
import { useStudentServiceCards } from './useStudentServiceCards';

const StudentServiceListings = () => {
  const serviceListingHeadingRef = useRef<HTMLDivElement>(null);
  const [currentUser] = useCurrentUser();
  const currentUserId = currentUser?.id ?? '';
  const { trackEvent } = useEventTracker();
  const { isLoadingCountries, countries } = useCountries();
  const { studentDetails, isLoadingStudentDetails } = useGetStudentDetails(currentUser?.id ?? '');
  const currentUserNationality = studentDetails?.student?.nationality;
  const [filters, setFilters] = useState<{
    nationality?: string;
    destination?: string[];
  }>({});
  const { selectedChips, toggleChip, filterOptions, selectedCardsData } = useStudentServiceCards({
    filters,
    agencyCountry:
      filters.nationality ||
      (Array.isArray(currentUserNationality) ? currentUserNationality[0]?.code : undefined) ||
      '',
  });

  const countryOptions = useMemo(() => {
    return countries.map(country => ({
      value: country.code,
      label: country.name,
    }));
  }, [countries]);

  const destinationOptions = DESTINATION_COUNTRIES_OPTIONS_LIST;

  useEffect(() => {
    if (currentUserNationality) {
      setFilters(filters => {
        // currentUserNationality is an IsoCountry[], take the first one's code
        const nationalityCode = Array.isArray(currentUserNationality)
          ? currentUserNationality[0]?.code
          : undefined;
        return { ...filters, nationality: nationalityCode };
      });
    }
  }, [currentUserNationality]);

  useEffect(() => {
    serviceListingHeadingRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [selectedChips]);

  const isLoadingInitial = isLoadingCountries || isLoadingStudentDetails;

  // Extract nationality code for USD notice check
  const currentUserNationalityCode = Array.isArray(currentUserNationality)
    ? currentUserNationality[0]?.code
    : undefined;

  const showUsdNotice = shouldShowUsdPricingNotice(
    filters,
    currentUserNationalityCode,
    selectedChips,
  );

  // Track once when visible
  useEffect(() => {
    if (!showUsdNotice) return;
    trackEvent({
      event: 'VAS - Student Hub Page - USD Notice Viewed',
      data: {
        student_id: currentUserId,
        nationality: filters?.nationality,
        defaultCountry: currentUserNationalityCode,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showUsdNotice]);

  return (
    <Flex direction="column" gap={4}>
      {isLoadingInitial && (
        <Flex align="center" justify="center">
          <Spinner size="xl" />
        </Flex>
      )}
      {!isLoadingInitial && (
        <Box mb={1} ref={serviceListingHeadingRef}>
          <div style={{ marginBottom: 0 }}>
            <Heading level={2} variant="titleL">
              Explore our Value Added Solutions
            </Heading>
          </div>
          <div style={{ marginTop: 0 }}>
            <Text variant="bodyM" intent="secondary" contrast="mid">
              Click through each category for more details
            </Text>
          </div>
        </Box>
      )}

      {!isLoadingInitial && (
        <Flex direction={{ xs: 'column', md: 'row' }} gap={4} pb={2}>
          <Combobox
            aria-label="Nationality"
            intent="secondary"
            label="Nationality"
            labelPosition="top"
            placeholderVariant="truncate"
            onChange={(value: string) => {
              setFilters({ ...filters, nationality: value || undefined });
              trackEvent({
                event: 'VAS - Vas Hub Page - Nationality Filtered',
                data: { nationality: value },
              });
            }}
            value={filters?.nationality ?? ''}
          >
            {countryOptions.map(country => (
              <Combobox.Option
                key={String(country.value)}
                label={country.label}
                value={String(country.value)}
              />
            ))}
          </Combobox>

          <MultiCombobox
            aria-label="Destination"
            intent="secondary"
            label="Destination"
            labelPosition="top"
            placeholderVariant="indicator"
            onChange={(values: string[]) => {
              setFilters({ ...filters, destination: values });
              trackEvent({
                event: 'VAS - Vas Hub Page - Destination Filtered',
                data: { destination: values, student_id: currentUserId },
              });
            }}
            value={filters?.destination ?? []}
          >
            {destinationOptions.map(country => (
              <MultiCombobox.Option
                key={String(country.value)}
                label={country.label}
                value={String(country.value)}
              />
            ))}
          </MultiCombobox>
        </Flex>
      )}

      {!isLoadingInitial && (
        <Card>
          <ServiceCardsSection
            chipOptions={filterOptions}
            selectedChips={selectedChips}
            toggleChip={toggleChip}
            selectedCardsData={selectedCardsData}
            isLoading={false}
            persona="student"
            showUsdNotice={showUsdNotice}
          />
        </Card>
      )}
    </Flex>
  );
};

export default StudentServiceListings;
